import { useEffect, useState } from 'react';
import { useTheme } from '@/shared/hooks/use-theme';

/**
 * Chrome dos gráficos ligado ao contrato de tokens (PBI 84883).
 *
 * O ECharts renderiza em canvas, que não resolve `var(--x)` — a cor precisa
 * chegar já calculada. Este módulo lê os tokens computados do `<html>` e os
 * devolve como cores CSS prontas, para que eixo, grade, tooltip e legenda
 * sigam o tema sem nenhuma cor fixa no meio do caminho.
 *
 * Fora daqui ficam as cores de SÉRIE (PRODGY_CHART_COLORS): aquilo é cor de
 * dado, não de tema, e não muda com a superfície.
 */

/** Lê um token do contrato e devolve uma cor CSS utilizável pelo canvas. */
const token = (name: string, alpha?: number): string => {
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(`--${name}`)
    .trim();
  if (!raw) return 'transparent';
  return alpha === undefined ? `hsl(${raw})` : `hsl(${raw} / ${alpha})`;
};

export interface ChartTheme {
  /** Transparente de propósito: quem pinta a superfície é o card do tema. */
  background: string;
  axisLine: string;
  axisLabel: string;
  splitLine: string;
  tooltipBg: string;
  tooltipBorder: string;
  tooltipText: string;
  legendText: string;
  /** Texto de ênfase primária dentro do gráfico (valor de medidor). */
  text: string;
  /** Trilho de fundo de medidores. */
  track: string;
  /** Trilho de medidor com transparência, para faixas empilhadas. */
  trackSoft: string;
  /** Quase invisível — véu sobre o trilho, não uma cor de leitura. */
  trackFaint: string;
  /** Extremo escuro da rampa neutra de medidor (zona de perigo). */
  deep: string;
}

const read = (): ChartTheme => ({
  background: 'transparent',
  axisLine: token('input'),
  axisLabel: token('text-muted'),
  splitLine: token('hairline'),
  tooltipBg: token('popover'),
  tooltipBorder: token('border'),
  tooltipText: token('popover-foreground'),
  legendText: token('text-muted'),
  text: token('foreground'),
  track: token('muted'),
  trackSoft: token('muted', 0.5),
  trackFaint: token('muted', 0.03),
  deep: token('surface-sunken'),
});

/**
 * Tokens do tema corrente, recalculados quando o tema troca.
 *
 * A leitura acontece dentro de um `requestAnimationFrame` porque quem aplica a
 * classe `.dark` no `<html>` é um efeito do ThemeProvider — que, sendo pai,
 * roda DEPOIS dos efeitos dos filhos. Ler direto no efeito pegaria o tema
 * anterior; o rAF garante que a classe já está no DOM.
 */
export const useChartTheme = (): ChartTheme => {
  const { theme } = useTheme();
  const [chart, setChart] = useState<ChartTheme>(read);

  useEffect(() => {
    const id = requestAnimationFrame(() => setChart(read()));
    return () => cancelAnimationFrame(id);
  }, [theme]);

  return chart;
};
